import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadReportCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";
import { getCompletionByUuid } from '../../../api/route_completions';
import RouteCompletionViewModal from '../RouteCompletionViewModal';

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const RouteReports = ({ routeData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [podModal, setPodModal] = useState({ open: false, completion: null, route: null, loading: false });

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'job', label: 'Customer' },
        { key: 'route', label: 'Route' },
        { key: 'job_comments', label: 'Comments' },
        { key: 'driver', label: 'Driver' },
        { key: 'truck', label: 'Truck' },
        { key: 'costings', label: 'Cost' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                routeData.length = 0; // Clear routeData if password is invalid
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            routeData.length = 0; // Clear routeData if there's an error
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setOpenRequestPassword(false); // Close the modal
        }
    };
    

    const showData = () => {
        if (routeData && routeData.length > 0) {
            // Strip only the required columns
            const formatted = routeData.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'subbed_price') {
                        newEntry[key] = entry.is_subbed ? `£${entry.subbed_price ?? '0.00'}` : '';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                // Always copy subbed fields for fragment rendering
                newEntry.is_subbed = entry.is_subbed;
                newEntry.subbed_company = entry.subbed_company ?? '';
                newEntry.subbed_ref = entry.subbed_ref ?? '';
                newEntry.subbed_price = entry.subbed_price ?? '';
                // Preserve raw fields needed for the POD modal
                newEntry.route_uuid = entry.route_uuid;
                newEntry._raw = entry;
                return newEntry;
            });
            setTableData(formatted);
        }
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            return a[column]?.toString().localeCompare(b[column]?.toString()) || 0;
        });
        setTableData(sorted);
    };

    const handleFilter = (column, value) => {
        const filtered = routeData.filter(item => item[column] === value);
        setIsFiltered(true);

        // Format filtered data
        const formatted = filtered.map((entry) => {
            const newEntry = {};
            displayColumns.forEach(({ key }) => {
                if (key === 'date') {
                    const dateObj = new Date(entry[key]);
                    newEntry[key] = isNaN(dateObj)
                        ? entry[key]
                        : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                } else {
                    newEntry[key] = entry[key] ?? '—';
                }
            });
            return newEntry;
        });

        setTableData(formatted);
    };

    const clearFilter = () => {
        setIsFiltered(false);
        setTableData(routeData);
    };

    const handleRowClick = async (entry) => {
        if (!entry.route_uuid) return;
        setPodModal({ open: true, completion: null, route: entry._raw, loading: true });
        const result = await getCompletionByUuid(entry.route_uuid);
        if (result?.success && result?.completion) {
            setPodModal({ open: true, completion: result.completion, route: entry._raw, loading: false });
        } else {
            setPodModal({ open: false, completion: null, route: null, loading: false });
            showAlert({ message: result?.error || 'No POD record found for this route.', type: 'warning', buttons: [{ label: 'Close', type: 'primary' }] });
        }
    };

    // Calculate subbed jobs summary
    const subbedJobs = tableData.filter((row) => row.is_subbed);
    const subbedCount = subbedJobs.length;


    return (
        <div
            id={theme}
            style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '20px',
                minHeight: '100vh',
                backgroundColor: isLightTheme ? '#fff' : '#080808',
            }}
        >
            <div
                style={{
                    flex: 1,
                    padding: '20px',
                    backgroundColor: isLightTheme ? '#f0f0f0' : '#2a2a2a',
                    borderRadius: '10px',
                    boxShadow: isLightTheme
                        ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                        : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                    maxWidth: '90vw',
                    border: isLightTheme ? '1px solid #ddd' : '1px solid #444',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginBottom: '10px' }}>
                    <h3
                        onClick={isFiltered ? clearFilter : null}
                        style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: isLightTheme ? '#333' : '#fff',
                            marginBottom: '15px',
                            cursor: isFiltered ? 'pointer' : 'default',
                        }}
                    >
                        📋 Route Report {isFiltered && '(filtered — click to reset)'}
                    </h3>
                    {allowDownload && (
                    <Button
                        onClick={() => {downloadReportCSV(routeData);}}
                        style={{
                            backgroundColor: isLightTheme ? '#4CAF50' : '#66bb6a',
                            color: '#fff',
                            padding: '10px 20px',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            marginBottom: '10px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: isLightTheme
                                ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
                                : '0px 4px 8px rgba(255, 255, 255, 0.1)',
                            transition: 'background-color 0.3s',
                        }}
                    >
                        Download CSV
                    </Button>
                    )}
                </div>

                {/* Subbed jobs summary */}
                {subbedCount > 0 && (
                    <div style={{
                        background: 'rgba(252, 186, 63, 0.8)',
                        color: '#333',
                        padding: '10px',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        fontWeight: 600,
                        fontSize: '16px',
                        textAlign: 'center',
                        boxShadow: '0px 2px 6px rgba(0,0,0,0.07)'
                    }}>
                        Subbed Jobs: {subbedCount}
                    </div>
                )}

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr
                            style={{
                                backgroundColor: isLightTheme ? '#ddd' : '#555',
                                color: isLightTheme ? '#333' : '#fff',
                            }}
                        >
                            {displayColumns.map(({ key, label }) => (
                                <th
                                    key={key}
                                    style={{
                                        padding: '10px',
                                        borderBottom: isLightTheme ? '2px solid #000' : '1px solid #ddd',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => sortByColumn(key)}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((entry, index) => (
                            <React.Fragment key={index}>
                            <tr
                                key={index}
                                style={{
                                    backgroundColor: entry.is_subbed
                                        ? 'rgba(252, 186, 63, 0.8)'
                                        : isLightTheme
                                            ? index % 2 === 0
                                                ? '#f9f9f9'
                                                : '#eaeaea'
                                            : index % 2 === 0
                                                ? '#3a3a3a'
                                                : '#2e2e2e',
                                    color: entry.is_subbed ? '#333' : isLightTheme ? '#333' : '#ddd',
                                    cursor: entry.route_uuid ? 'pointer' : 'default',
                                }}
                                onClick={() => handleRowClick(entry)}
                                title={entry.route_uuid ? 'Click to view POD / completion details' : ''}
                            >
                                {displayColumns.map(({ key }) => (
                                    <td
                                        key={key}
                                        style={{
                                            padding: '10px',
                                            borderBottom: isLightTheme ? '2px solid #000' : '1px solid #ddd',
                                        }}
                                        onClick={() =>
                                            key !== 'costings'
                                                ? handleFilter(key, entry[key])
                                                : null
                                        }
                                    >
                                        {entry[key]}
                                    </td>
                                ))}
                            </tr>
                            {entry.is_subbed && (
                                <tr style={{ height: '10px', borderBottom: isLightTheme ? '2px solid #000' : '1px solid #ddd' }}>
                                    <td colSpan={displayColumns.length} style={{ backgroundColor: theme === 'light' ? '#f9f9f9' : '#3a3a3a', padding: '4px 16px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <span style={{ marginRight: 24 }}>Subbed To: <strong>{entry.subbed_company || 'N/A'}</strong></span>
                                            <span style={{ marginRight: 24 }}>Price: <strong>£{entry.subbed_price || 'N/A'}</strong></span>
                                            <span>Ref: <strong>{entry.subbed_ref || 'N/A'}</strong></span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* POD/Completion modal */}
            {podModal.open && (
                podModal.loading ? (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ background: isLightTheme ? '#fff' : '#222', padding: 32, borderRadius: 10, color: isLightTheme ? '#222' : '#fff' }}>Loading POD data...</div>
                    </div>
                ) : (
                    <RouteCompletionViewModal
                        completion={podModal.completion}
                        route={podModal.route}
                        theme={theme}
                        onClose={() => setPodModal({ open: false, completion: null, route: null, loading: false })}
                    />
                )
            )}

            {/* Render the RequestPassword modal only if openRequestPassword is true */}
            {openRequestPassword && (
                <RequestPassword
                    theme={theme}
                    isOpen={openRequestPassword}
                    setIsOpen={setOpenRequestPassword}
                    returnData={(value) => handlePasswordSubmission(value)}
                />
            )}
        </div>
    );
};

export default RouteReports;
